import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-scroll';
import { useLanguage } from '../context/LanguageContext';
import { nav } from '../content/portfolio';

const DEFAULT_CHAPTER = { ink: '#e8e6e1', field: '#0c0b09' };

/** The bar takes the ink and field of whichever chapter (`data-ink`, `data-field`) sits under it. */
const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [chapter, setChapter] = useState(DEFAULT_CHAPTER);
  const headerRef = useRef<HTMLElement>(null);
  const { language, setLanguage } = useLanguage();
  const items = nav[language];

  useEffect(() => {
    const read = () => {
      const stack = document.elementsFromPoint ? document.elementsFromPoint(window.innerWidth / 2, 80) : [];
      const probe = stack.find((el) => !headerRef.current?.contains(el) && el.closest('[data-ink]'));
      const host = probe?.closest<HTMLElement>('[data-ink]');
      setChapter(
        host
          ? { ink: host.dataset.ink || DEFAULT_CHAPTER.ink, field: host.dataset.field || DEFAULT_CHAPTER.field }
          : DEFAULT_CHAPTER
      );
    };
    read();
    window.addEventListener('scroll', read, { passive: true });
    window.addEventListener('resize', read);
    return () => {
      window.removeEventListener('scroll', read);
      window.removeEventListener('resize', read);
    };
  }, []);

  const linkClass = 'cursor-pointer px-3 py-2 text-base opacity-80 transition-opacity hover:opacity-100';

  const langSwitch = (
    <div className="flex items-center text-base" role="group" aria-label="Language">
      {(['pt', 'en'] as const).map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => setLanguage(lang)}
          aria-pressed={language === lang}
          className={`px-2 py-1 uppercase transition-opacity ${language === lang ? 'font-semibold' : 'opacity-60 hover:opacity-100'}`}
        >
          {lang}
        </button>
      ))}
    </div>
  );

  return (
    <header
      ref={headerRef}
      className="fixed inset-x-0 top-0 z-50 transition-colors duration-300"
      style={{ backgroundColor: chapter.field, color: chapter.ink }}
    >
      <div className="container-responsive">
        <div className="flex h-16 items-center justify-between">
          <Link to="home" smooth duration={600} className="cursor-pointer font-serif text-xl">
            Isaac Ribeiro Leão
          </Link>

          <nav className="hidden md:flex items-center gap-2" aria-label="Primary">
            {items.map((item) => (
              <Link key={item.id} to={item.id} smooth duration={600} offset={-64} className={linkClass}>
                {item.label}
              </Link>
            ))}
            {langSwitch}
          </nav>

          <div className="flex items-center gap-2 md:hidden">
            {langSwitch}
            <button
              type="button"
              onClick={() => setIsMenuOpen((o) => !o)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-nav"
              className="px-3 py-2 text-base"
            >
              {isMenuOpen ? (language === 'pt' ? 'Fechar' : 'Close') : 'Menu'}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <nav id="mobile-nav" className="md:hidden pb-6" aria-label="Primary">
            <ul className="flex flex-col gap-1">
              {items.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.id}
                    smooth
                    duration={600}
                    offset={-64}
                    onClick={() => setIsMenuOpen(false)}
                    className="block cursor-pointer py-3 font-serif text-3xl"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
